import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MessageCenter } from './MessageCenter';
import { NotificationsPanel } from './NotificationsPanel';
import { QueueItemsPanel } from './QueueItemsPanel';
import { SOWDetailsModal } from './SOWDetailsModal';
import { EngagementNotificationPanel } from './messaging/EngagementNotificationPanel';
import { FocusModeSettingsDialog } from './messaging/FocusModeSettings';
import { useActionItemsUnified } from '../hooks/useActionItemsUnified';
import { projectId as pid } from '../utils/supabase/info';
import { cachedFetch } from '../utils/fetchWithCache';
import { fetchUserPreferences, inAppUnreadCount } from '../utils/notificationPreferences';
import type { UserNotificationPreferences } from '../types';

interface NotificationBellProps {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  companyId: string;
  projectId: string;
  publicAnonKey: string;
  accessToken?: string;
  /** SPA navigation callback (avoids full page reload) */
  onNavigate?: (url: string) => void;
}

export function NotificationBell({ userId, userName, userEmail, userRole, companyId, projectId, publicAnonKey, accessToken, onNavigate }: NotificationBellProps) {
  const [showMessageCenter, setShowMessageCenter] = useState(false);
  const [showEngagementNotifs, setShowEngagementNotifs] = useState(false);
  const [showFocusMode, setShowFocusMode] = useState(false);
  const [showQueueItems, setShowQueueItems] = useState(false);
  const [selectedSOWId, setSelectedSOWId] = useState<string | null>(null);
  const [engagementUnreadCount, setEngagementUnreadCount] = useState(0);

  // Check if user is a firm user (consultant, cfAdmin, billingAdmin, bdRep, EL)
  const firmRoles = [
    'consultant', 'cfAdmin', 'billingAdmin', 'bdRep', 'CFConsultant',
    'consulting_firm_admin', 'billing_admin', 'bd_rep',
    'Engagement Lead', 'engagement_lead',
  ];
  const isFirmUser = firmRoles.includes(userRole);
  
  // System admins should not see notifications
  const isSystemAdmin = userRole === 'systemAdmin' || userRole === 'system_admin';
  
  // Use shared hook for legacy notification count
  const { 
    unreadCount: legacyUnreadCount, 
    refresh,
  } = useActionItemsUnified({
    accessToken: accessToken || publicAnonKey,
    currentUser: { id: userId, role: userRole },
    companyId: isFirmUser ? undefined : companyId,
  });

  // Per-user notifications (messages, follow-ups, assignments, mentions) live in
  // `notification:{userId}:*`. The legacy company store the hook above reads is
  // never written for clients, so count this store too or the badge stays at 0.
  // Firm users get them too (mentions, digests, SOW alignment): they open from the
  // engagement panel's Updates button. Categories turned off in-app are not counted.
  const [userNotifUnread, setUserNotifUnread] = useState(0);
  const preferencesRef = useRef<UserNotificationPreferences | null>(null);
  useEffect(() => {
    if (isSystemAdmin) return;
    fetchUserPreferences(userId, pid, '').then((p) => { preferencesRef.current = p; }).catch(() => {});
  }, [userId]);
  const fetchUserNotifUnread = async () => {
    if (isSystemAdmin) return;
    try {
      const response = await cachedFetch(
        `https://${pid}.supabase.co/functions/v1/make-server-e90c59cd/user/notifications?userId=${encodeURIComponent(userId)}`,
        { headers: { Authorization: `Bearer ${accessToken || publicAnonKey}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const prefs = preferencesRef.current;
        setUserNotifUnread(prefs && Array.isArray(data.notifications) ? inAppUnreadCount(data.notifications, prefs) : Number(data.unreadCount) || 0);
      }
    } catch {
      // Non-blocking
    }
  };

  // Also fetch engagement messaging unread count
  useEffect(() => {
    if (isSystemAdmin) return;
    // Defer initial fetch so the critical dashboard bundle gets connection
    // priority during page load.
    const initial = setTimeout(() => { fetchEngagementUnread(); fetchUserNotifUnread(); }, 2000);
    const interval = setInterval(() => { fetchEngagementUnread(); fetchUserNotifUnread(); }, 30000);
    return () => { clearTimeout(initial); clearInterval(interval); };
  }, [userId]);

  const fetchEngagementUnread = async () => {
    try {
      const response = await cachedFetch(
        `https://${pid}.supabase.co/functions/v1/make-server-e90c59cd/engagement-messaging/notifications?userId=${userId}`,
        { headers: { Authorization: `Bearer ${accessToken || publicAnonKey}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setEngagementUnreadCount(data.unreadCount || 0);
      }
    } catch {
      // Non-blocking
    }
  };

  // Poll for updates every 30 seconds
  useEffect(() => {
    if (isSystemAdmin) return;
    const interval = setInterval(() => {
      refresh();
    }, 30000);
    return () => clearInterval(interval);
  }, [refresh, isSystemAdmin]);

  // Combined unread count
  const totalUnread = legacyUnreadCount + engagementUnreadCount + userNotifUnread;

  // Refresh unread count when panel closes
  const handlePanelClose = () => {
    setShowMessageCenter(false);
    refresh();
    fetchEngagementUnread();
    fetchUserNotifUnread();
  };

  const handleEngagementPanelClose = () => {
    setShowEngagementNotifs(false);
    fetchEngagementUnread();
  };

  // Unified navigation helper: prefer SPA navigate, fallback to full reload
  const navigateTo = (url: string) => {
    if (onNavigate) {
      onNavigate(url);
    } else {
      window.location.href = url;
    }
  };

  // Bell click: show engagement notifications (primary), fallback to legacy
  const handleBellClick = () => {
    if (isFirmUser) {
      // For firm users: show engagement notification panel
      setShowEngagementNotifs(true);
    } else if (engagementUnreadCount > 0) {
      // For client users with unread engagement messages: show engagement panel
      setShowEngagementNotifs(true);
    } else {
      // For client users: show message center
      setShowMessageCenter(true);
    }
  };

  return (
    <>
      <div className="relative">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBellClick}
          className="relative"
          disabled={isSystemAdmin}
        >
          <Bell className="h-5 w-5" />
          {totalUnread > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {totalUnread > 9 ? '9+' : totalUnread}
            </Badge>
          )}
        </Button>
      </div>

      {/* Engagement Notification Panel (firm users) */}
      {showEngagementNotifs && isFirmUser && (
        <EngagementNotificationPanel
          userId={userId}
          userRole={userRole}
          accessToken={accessToken || publicAnonKey}
          onClose={handleEngagementPanelClose}
          onOpenThread={(engagementId, threadId) => {
            console.log('[NotificationBell] Deep-link to thread:', { engagementId, threadId });
            // Navigate to the engagement messaging page with thread context
            const url = `/engagements/${encodeURIComponent(engagementId)}/messages?threadId=${encodeURIComponent(threadId)}`;
            navigateTo(url);
          }}
          onOpenFocusMode={() => {
            setShowEngagementNotifs(false);
            setShowFocusMode(true);
          }}
          onOpenUpdates={() => {
            setShowEngagementNotifs(false);
            setShowMessageCenter(true);
          }}
          updatesUnread={userNotifUnread}
        />
      )}

      {/* Engagement Notification Panel (client users with engagement messages) */}
      {showEngagementNotifs && !isFirmUser && !isSystemAdmin && (
        <EngagementNotificationPanel
          userId={userId}
          userRole={userRole}
          accessToken={accessToken || publicAnonKey}
          onClose={handleEngagementPanelClose}
          onOpenThread={(engagementId, threadId) => {
            console.log('[NotificationBell:Client] Deep-link to thread:', { engagementId, threadId });
            // For client users, navigate to the messaging page
            const url = `/engagements/${encodeURIComponent(engagementId)}/messages?threadId=${encodeURIComponent(threadId)}`;
            navigateTo(url);
          }}
          onOpenFocusMode={() => {
            setShowEngagementNotifs(false);
            setShowFocusMode(true);
          }}
        />
      )}

      {/* A firm user's own notifications (mentions, digests, SOW alignment), from the engagement panel's Updates button */}
      {showMessageCenter && isFirmUser && (
        <NotificationsPanel
          userId={userId}
          projectId={projectId}
          accessToken={accessToken || publicAnonKey}
          onClose={handlePanelClose}
          onUnreadCountChange={() => {
             refresh(); 
             fetchUserNotifUnread();
          }}
          onOpenQueueItems={() => {
            setShowMessageCenter(false);
            setShowQueueItems(true);
          }}
          onOpenSOW={(sowId) => {
            setShowMessageCenter(false);
            setSelectedSOWId(sowId);
          }}
        />
      )}
      
      {/* Client MessageCenter */}
      {showMessageCenter && !isFirmUser && !isSystemAdmin && (
        <MessageCenter
          userId={userId}
          userName={userName}
          userEmail={userEmail}
          userRole={userRole}
          companyId={companyId}
          projectId={projectId}
          publicAnonKey={publicAnonKey}
          accessToken={accessToken}
          onClose={handlePanelClose}
        />
      )}

      {/* Focus Mode Settings Dialog */}
      <FocusModeSettingsDialog
        userId={userId}
        userRole={userRole}
        accessToken={accessToken || publicAnonKey}
        open={showFocusMode}
        onOpenChange={setShowFocusMode}
      />

      {/* Queue Items Panel */}
      <QueueItemsPanel
        isOpen={showQueueItems}
        onClose={() => setShowQueueItems(false)}
        accessToken={accessToken || publicAnonKey}
        projectId={projectId}
        userId={userId}
        userName={userName}
        userEmail={userEmail}
        userRole={userRole}
      />

      {/* SOW Details Modal */}
      <SOWDetailsModal
        sowId={selectedSOWId}
        isOpen={!!selectedSOWId}
        onClose={() => setSelectedSOWId(null)}
        accessToken={accessToken || publicAnonKey}
        projectId={projectId}
      />
    </>
  );
}